import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router';
import { Search, Info, ChevronRight, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import DataTable, { Column } from '@/components/DataTable';
import { YourCheckaNavigation } from '@/components/Navbar';
import ValidationModal from '@/components/modals/ValidationModal';
import PaymentModal from '@/components/modals/PaymentModal';
import DeleteInvestigationModal from '@/components/modals/DeleteInvestigationModal';
import SlaBadge from '@/components/SlaBadge';
import { activeSlaDeadline } from '@/utils/slaDeadline';
import {
    useGetMyInvestigationsQuery,
    useValidateInvestigationReportMutation,
    useVerifyPaymentMutation,
} from '@/store/api/apiSlice';
import { Investigation, InvestigationStatus, CheckType, UserRole } from '@/types';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';

interface OutletContext {
    onRequestInvestigation: () => void;
}

type FilterType = 'All' | InvestigationStatus;
type PaymentFilter = 'All' | 'ToPay' | 'Paid';

const paymentFilterToArg = (f: PaymentFilter): { isPaid?: boolean } | undefined => {
    if (f === 'ToPay') return { isPaid: false };
    if (f === 'Paid') return { isPaid: true };
    return undefined;
};

// Every status gets its own filter chip (plus "All"), so the user can narrow to
// exactly one status.
const STATUS_TABS: { value: FilterType; label: string }[] = [
    { value: 'All', label: 'All' },
    { value: InvestigationStatus.NEW, label: 'New' },
    { value: InvestigationStatus.PENDING, label: 'Pending' },
    { value: InvestigationStatus.AWAITING_APPROVAL, label: 'Awaiting Approval' },
    { value: InvestigationStatus.IN_PROGRESS, label: 'In Progress' },
    { value: InvestigationStatus.UNDER_REVIEW, label: 'Under Review' },
    { value: InvestigationStatus.COMPLETED, label: 'Completed' },
    { value: InvestigationStatus.CANCELLED, label: 'Cancelled' },
];

export default function Investigations() {
    const navigate = useNavigate();
    const currentUser = useAppSelector(selectCurrentUser);
    const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
    const isAgent = currentUser?.role === UserRole.AGENT;
    const { onRequestInvestigation } = useOutletContext<OutletContext>();
    const [filter, setFilter] = useState<FilterType>('All');
    const [paymentFilter, setPaymentFilter] = useState<PaymentFilter>('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [selectedInvestigation, setSelectedInvestigation] = useState<Investigation | null>(null);
    // The selection comes off the investigation so paying later charges the
    // same total it was created with. Without it the gateway would bill the
    // whole plan and any cherry-pick markup or add-ons would be lost.
    const [paymentTarget, setPaymentTarget] = useState<{
        id: string;
        checkType: CheckType;
        features: string[];
        addons: string[];
    } | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Investigation | null>(null);

    // Fetch investigations from API. Each tab caches independently because the
    // `isPaid` arg is part of the RTK cache key.
    const { data: investigations = [], refetch: refetchInvestigations } = useGetMyInvestigationsQuery(
        paymentFilterToArg(paymentFilter),
    );
    const [validateReport, { isLoading: isValidating }] = useValidateInvestigationReportMutation();
    const [verifyPayment] = useVerifyPaymentMutation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [paymentBanner, setPaymentBanner] = useState<{ kind: 'success' | 'pending' | 'failed'; text: string } | null>(
        null,
    );

    // Handle return from the hosted checkout. Monnify redirects back with
    // ?paymentReference=<ref> (and sometimes ?paymentStatus=…). We also still accept the
    // legacy ?payment=success&tx_ref=… shape for backwards compatibility.
    useEffect(() => {
        // Monnify's reference param is the canonical one; fall back to legacy names.
        const paymentRef =
            searchParams.get('paymentReference') || searchParams.get('tx_ref') || searchParams.get('transaction_id');
        const isPaymentReturn = paymentRef !== null || searchParams.get('payment') === 'success';
        if (!isPaymentReturn) return;

        const gatewayStatus = (searchParams.get('paymentStatus') || searchParams.get('status') || '').toLowerCase();

        // Strip payment-related params so a refresh doesn't re-trigger this effect.
        const next = new URLSearchParams(searchParams);
        ['payment', 'tx_ref', 'transaction_id', 'status', 'paymentReference', 'paymentStatus'].forEach((k) =>
            next.delete(k),
        );
        setSearchParams(next, { replace: true });

        // Refresh immediately — webhook may have already landed.
        refetchInvestigations();

        // Only treat as failed when the gateway explicitly reports a non-success status.
        const SUCCESS_STATES = ['successful', 'completed', 'paid', 'success'];
        if (gatewayStatus && !SUCCESS_STATES.includes(gatewayStatus)) {
            setPaymentBanner({
                kind: 'failed',
                text: 'Payment was not completed. You can retry from the investigation details.',
            });
            return;
        }

        setPaymentBanner({
            kind: 'pending',
            text: 'Confirming your payment...',
        });

        if (!paymentRef) {
            // No reference to verify against; rely on webhook + the immediate refetch above.
            setPaymentBanner({ kind: 'success', text: 'Payment received. Employee invitation will be sent shortly.' });
            return;
        }

        // Belt-and-braces verify; webhook will land regardless, so any error here is non-fatal.
        verifyPayment({ tx_ref: paymentRef })
            .unwrap()
            .then((res) => {
                if (res?.status === 'successful') {
                    setPaymentBanner({
                        kind: 'success',
                        text: 'Payment successful. Employee invitation has been sent.',
                    });
                } else {
                    setPaymentBanner({
                        kind: 'pending',
                        text: 'Payment recorded. We’ll update the investigation as soon as it’s confirmed.',
                    });
                }
            })
            .catch(() => {
                // Verify failed (likely 401 from a lapsed session, or transient). Webhook will still link the payment.
                setPaymentBanner({
                    kind: 'pending',
                    text: 'Payment recorded. We’ll update the investigation as soon as it’s confirmed.',
                });
            })
            .finally(() => {
                refetchInvestigations();
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Get user initials
    const getInitials = (firstName?: string, lastName?: string) => {
        const first = firstName?.charAt(0) || '';
        const last = lastName?.charAt(0) || '';
        return (first + last).toUpperCase() || 'U';
    };

    const filteredData = investigations.filter((item) => {
        const matchesFilter = filter === 'All' || item.status === filter;
        const fullName = `${item.employeeFirstName} ${item.employeeLastName}`.toLowerCase();
        const matchesSearch = fullName.includes(searchQuery.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    // Pagination
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const emptyStateText =
        searchQuery.trim() !== ''
            ? 'No investigations match your search.'
            : paymentFilter === 'ToPay'
              ? 'All caught up — no investigations awaiting payment.'
              : paymentFilter === 'Paid'
                ? 'No paid investigations yet.'
                : 'No investigations found';

    const handleRowSelect = (index: number) => {
        const newSelected = new Set(selectedRows);
        if (newSelected.has(index)) {
            newSelected.delete(index);
        } else {
            newSelected.add(index);
        }
        setSelectedRows(newSelected);
    };

    const handleOpenValidationModal = (investigation: Investigation) => {
        setSelectedInvestigation(investigation);
        setShowValidationModal(true);
    };

    const handleRowClick = (item: Investigation) => {
        if (!item.isPaid) {
            setPaymentTarget({
                id: item.id,
                checkType: item.checkType,
                features: item.featureIds ?? [],
                addons: item.addons ?? [],
            });
            return;
        }
        navigate(`/investigations/${item.id}`);
    };

    const handlePaymentFilterChange = (next: PaymentFilter) => {
        setPaymentFilter(next);
        setCurrentPage(1);
    };

    const handleValidationSubmit = async (approved: boolean, validationNotes?: string) => {
        if (!selectedInvestigation) return;

        try {
            await validateReport({
                id: selectedInvestigation.id,
                approved,
                ...(validationNotes && { validationNotes }),
            }).unwrap();

            setShowValidationModal(false);
            setSelectedInvestigation(null);

            const message = approved
                ? 'Investigation approved successfully! Earning created.'
                : 'Investigation rejected. Agent will be notified to revise.';
            alert(message);
        } catch (error: any) {
            console.error('Failed to validate investigation:', error);
            alert(error?.data?.message || 'Failed to validate investigation');
        }
    };

    const columns: Column<Investigation>[] = [
        {
            key: 'id',
            label: 'S/N',
            sortable: true,
            render: (_item, index) => (
                <span className="text-sm text-gray-700">{(currentPage - 1) * rowsPerPage + index + 1}</span>
            ),
        },
        {
            key: 'employeeFirstName',
            label: 'Employee Name',
            sortable: true,
            hasMenu: true,
            render: (item) => (
                <span className="text-sm text-gray-900">
                    {item.employeeFirstName} {item.employeeLastName}
                </span>
            ),
        },
        {
            key: 'checkType',
            label: 'Check Type',
            sortable: true,
            hasMenu: true,
            render: (item) => (
                <Badge
                    variant="outline"
                    className={`${
                        item.checkType === CheckType.STANDARD
                            ? 'bg-indigo-50 text-indigo-600 border-indigo-100'
                            : 'bg-orange-50 text-[#FF6B35] border-orange-100'
                    } px-4 py-1 rounded-lg font-medium`}
                >
                    {item.checkType}
                </Badge>
            ),
        },
        {
            key: 'createdAt',
            label: 'Date Requested',
            sortable: true,
            hasMenu: true,
            render: (item) => (
                <span className="text-sm text-gray-700">{new Date(item.createdAt).toLocaleDateString()}</span>
            ),
        },
        {
            key: 'status',
            label: 'Status',
            sortable: true,
            hasMenu: true,
            render: (item) => {
                const isCompleted = item.status === InvestigationStatus.COMPLETED;
                return (
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge
                            variant="outline"
                            className={`${
                                isCompleted
                                    ? 'bg-green-50 text-green-700 border-green-100'
                                    : 'bg-yellow-50 text-yellow-700 border-yellow-100'
                            } px-4 py-1 rounded-lg font-medium`}
                        >
                            {item.status.replace('_', ' ')}
                        </Badge>
                        {!item.isPaid && (
                            <Badge
                                variant="outline"
                                className="bg-amber-50 text-amber-700 border-amber-200 px-3 py-1 rounded-lg font-medium"
                            >
                                Payment due
                            </Badge>
                        )}
                        <SlaBadge deadline={activeSlaDeadline(item)} />
                    </div>
                );
            },
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (item) => (
                <div className="flex items-center gap-2">
                    {!item.isPaid ? (
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                setPaymentTarget({
                                    id: item.id,
                                    checkType: item.checkType,
                                    features: item.featureIds ?? [],
                                    addons: item.addons ?? [],
                                });
                            }}
                            className="bg-[#FF6B35] hover:bg-[#e0581f] text-white px-6 py-2 rounded-xl text-sm font-medium"
                        >
                            Pay now
                        </Button>
                    ) : (
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/investigations/${item.id}`);
                            }}
                            className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2 rounded-xl text-sm font-medium"
                        >
                            View Details
                        </Button>
                    )}
                    {isSuperAdmin && (
                        <button
                            type="button"
                            title="Delete investigation"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(item);
                            }}
                            className="flex items-center justify-center w-9 h-9 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                {/* Header/Navbar */}
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={3}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'User'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'U'}
                    only={true}
                    className="border-b border-gray-100"
                />

                {/* Content */}
                <div className="flex-1 overflow-hidden p-4 md:p-8 lg:p-10 flex flex-col">
                    {/* Header */}
                    <div className="mb-4 md:mb-6">
                        {paymentBanner && (
                            <div
                                className={`mb-4 flex items-start justify-between gap-3 rounded-xl border p-4 ${
                                    paymentBanner.kind === 'success'
                                        ? 'border-green-200 bg-green-50 text-green-800'
                                        : paymentBanner.kind === 'failed'
                                          ? 'border-red-200 bg-red-50 text-red-700'
                                          : 'border-yellow-200 bg-yellow-50 text-yellow-800'
                                }`}
                            >
                                <p className="text-sm leading-relaxed">{paymentBanner.text}</p>
                                <button
                                    type="button"
                                    onClick={() => setPaymentBanner(null)}
                                    className="text-sm font-medium underline-offset-2 hover:underline"
                                >
                                    Dismiss
                                </button>
                            </div>
                        )}
                        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 text-start">
                            Investigations
                        </h1>
                        <div className="flex items-center justify-center gap-2 text-gray-600">
                            <Info className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                            <p className="text-sm leading-relaxed text-start">
                                View and manage all background check investigations. Track the status of ongoing checks,
                                review completed reports, and take action on investigations awaiting approval. Use the
                                filters to quickly find specific investigations or search by employee name.
                            </p>
                        </div>
                    </div>

                    {/* Payment-status tabs */}
                    <div className="mb-4 flex items-center gap-2 overflow-x-auto">
                        {(['All', 'ToPay', 'Paid'] as const).map((tab) => {
                            const active = paymentFilter === tab;
                            const label = tab === 'ToPay' ? 'To Pay' : tab;
                            return (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => handlePaymentFilterChange(tab)}
                                    className={`px-4 md:px-5 py-2 md:py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                                        active
                                            ? tab === 'ToPay'
                                                ? 'bg-[#FF6B35] text-white'
                                                : 'bg-gray-900 text-white'
                                            : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Search and Filters */}
                    <div className="flex flex-col gap-3 mb-4 md:mb-6">
                        {/* Row 1: search + New Investigation */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="relative w-full sm:max-w-xs">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by employee name"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
                                />
                            </div>
                            {/* New Investigation Button — agents don't create investigations */}
                            {!isAgent && (
                                <Button
                                    onClick={onRequestInvestigation}
                                    className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap w-full sm:w-auto"
                                >
                                    New Investigation
                                </Button>
                            )}
                        </div>

                        {/* Row 2: status chips — wrap to new lines on any width (no horizontal scroll) */}
                        <div className="flex flex-wrap items-center gap-2">
                            {STATUS_TABS.map((tab) => (
                                <button
                                    key={tab.value}
                                    onClick={() => setFilter(tab.value)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                                        filter === tab.value
                                            ? 'bg-gray-900 text-white'
                                            : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Mobile Card List */}
                    <div className="md:hidden flex-1 min-h-0 overflow-y-auto space-y-3">
                        {filteredData.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">{emptyStateText}</p>
                        ) : (
                            filteredData.map((investigation) => {
                                const isCompleted = investigation.status === InvestigationStatus.COMPLETED;
                                return (
                                    <div
                                        key={investigation.id}
                                        onClick={() => handleRowClick(investigation)}
                                        className="w-full text-left border border-gray-100 rounded-xl p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer"
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-gray-900">
                                                {investigation.employeeFirstName} {investigation.employeeLastName}
                                            </span>
                                            <div className="flex items-center gap-2 shrink-0">
                                                {isSuperAdmin && (
                                                    <button
                                                        type="button"
                                                        title="Delete investigation"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDeleteTarget(investigation);
                                                        }}
                                                        className="flex items-center justify-center w-8 h-8 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <ChevronRight className="w-4 h-4 text-gray-400" />
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Badge
                                                variant="outline"
                                                className={`${
                                                    investigation.checkType === CheckType.STANDARD
                                                        ? 'bg-indigo-50 text-indigo-600 border-indigo-100'
                                                        : 'bg-orange-50 text-[#FF6B35] border-orange-100'
                                                } px-2 py-0.5 rounded-lg font-medium text-xs capitalize`}
                                            >
                                                {investigation.checkType}
                                            </Badge>
                                            <Badge
                                                variant="outline"
                                                className={`${
                                                    isCompleted
                                                        ? 'bg-green-50 text-green-700 border-green-100'
                                                        : 'bg-yellow-50 text-yellow-700 border-yellow-100'
                                                } px-2 py-0.5 rounded-lg font-medium text-xs`}
                                            >
                                                {investigation.status.replace('_', ' ')}
                                            </Badge>
                                            {!investigation.isPaid && (
                                                <Badge
                                                    variant="outline"
                                                    className="bg-amber-50 text-amber-700 border-amber-200 px-2 py-0.5 rounded-lg font-medium text-xs"
                                                >
                                                    Payment due
                                                </Badge>
                                            )}
                                            <SlaBadge deadline={activeSlaDeadline(investigation)} />
                                            <span className="text-xs text-gray-500">
                                                {new Date(investigation.createdAt).toLocaleDateString('en-GB', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric',
                                                })}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Desktop Table Container */}
                    <div className="hidden md:block border border-gray-100 rounded-2xl overflow-hidden flex-1 min-h-0">
                        <div className="h-full flex flex-col">
                            {filteredData.length === 0 ? (
                                <p className="text-center text-gray-500 py-12">{emptyStateText}</p>
                            ) : (
                                <DataTable
                                    data={filteredData}
                                    columns={columns}
                                    showCheckbox={true}
                                    selectedRows={selectedRows}
                                    onRowSelect={handleRowSelect}
                                    onRowClick={handleRowClick}
                                    hoverable={true}
                                    striped={true}
                                />
                            )}
                        </div>
                    </div>

                    {/* Pagination */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
                        {/* Rows per page */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Rows per page:</span>
                            <select
                                value={rowsPerPage}
                                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                        </div>

                        {/* Page numbers */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                ‹
                            </button>

                            <button className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50">
                                1
                            </button>

                            <span className="px-2 text-gray-400">...</span>

                            <button className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50">
                                11
                            </button>

                            <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#FF6B35] text-white">
                                {currentPage}
                            </button>

                            <button className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50">
                                13
                            </button>

                            <span className="px-2 text-gray-400">...</span>

                            <button className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50">
                                {totalPages}
                            </button>

                            <button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                ›
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Validation Modal */}
            <ValidationModal
                isOpen={showValidationModal}
                onClose={() => {
                    setShowValidationModal(false);
                    setSelectedInvestigation(null);
                }}
                onSubmit={handleValidationSubmit}
                isLoading={isValidating}
                investigationId={selectedInvestigation?.id || ''}
                employeeName={
                    selectedInvestigation
                        ? `${selectedInvestigation.employeeFirstName} ${selectedInvestigation.employeeLastName}`
                        : ''
                }
            />

            {/* Payment Modal — opened from a "Pay now" row on the To Pay / All tab */}
            {paymentTarget && (
                <PaymentModal
                    isOpen
                    onClose={() => setPaymentTarget(null)}
                    onBack={() => setPaymentTarget(null)}
                    checkType={paymentTarget.checkType}
                    investigationId={paymentTarget.id}
                    features={paymentTarget.features}
                    addons={paymentTarget.addons}
                />
            )}

            {/* Delete confirmation (Super Admin only) — phrase + OTP gated */}
            {deleteTarget && isSuperAdmin && (
                <DeleteInvestigationModal
                    isOpen
                    investigationId={deleteTarget.id}
                    employeeName={`${deleteTarget.employeeFirstName} ${deleteTarget.employeeLastName}`}
                    onClose={() => setDeleteTarget(null)}
                    onDeleted={() => {
                        setDeleteTarget(null);
                        refetchInvestigations();
                    }}
                />
            )}
        </main>
    );
}
